import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Package, 
  Package2,
  Loader2, 
  RefreshCw,
  DollarSign,
  Activity,
  Clock,
  Download,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  finishedGoodsInventoryApiService,
  FinishedGoodsInventorySummaryResponse,
  FinishedGoodsCategorySummary,
  FinishedGoodsInventoryExtendedSummary,
  FinishedGoodsInventoryAgingSummary,
  FinishedGoodsInventoryAgingDetail
} from '../services/finishedGoodsInventoryApi';
import { useBranch } from '../contexts/BranchContext';
import { 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell
} from 'recharts';

const COLORS = ['#003D64', '#0568BF', '#FD6500', '#10B981', '#8B5CF6'];
type AgingRow = FinishedGoodsInventoryAgingSummary & {
  agingBucket?: string;
  categoryName?: string | null;
};

const FinishedGoodsDashboard: React.FC = () => {
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<FinishedGoodsInventorySummaryResponse | null>(null);
  const [categorySummary, setCategorySummary] = useState<FinishedGoodsCategorySummary[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [extendedInventory, setExtendedInventory] = useState<FinishedGoodsInventoryExtendedSummary[]>([]);
  const [extendedLoading, setExtendedLoading] = useState(false);
  const [extendedPage, setExtendedPage] = useState(1);
  const [extendedSearch, setExtendedSearch] = useState('');
  const EXTENDED_PAGE_SIZE = 25;
  const [agingPage, setAgingPage] = useState(1);
  const AGING_PAGE_SIZE = 25;
  const [agingRows, setAgingRows] = useState<AgingRow[]>([]);
  const [agingLoading, setAgingLoading] = useState(false);
  const [agingDetailRows, setAgingDetailRows] = useState<FinishedGoodsInventoryAgingDetail[]>([]);
  const [agingDetailLoading, setAgingDetailLoading] = useState(false);
  const [agingSearch, setAgingSearch] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, [selectedBranch]);

  useEffect(() => {
    setExtendedPage(1);
    loadExtendedInventory();
  }, [selectedBranch]);

  useEffect(() => {
    setExtendedPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    setAgingPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    loadAgingSummary();
  }, [selectedBranch, selectedCategory, categorySummary]);

  useEffect(() => {
    loadAgingDetails();
  }, [selectedBranch, selectedCategory, categorySummary]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const branchId = selectedBranch || undefined;

      // Load dashboard data in parallel - summary includes activeEndgoods
      const results = await Promise.allSettled([
        finishedGoodsInventoryApiService.getInventorySummary(branchId),
        finishedGoodsInventoryApiService.getInventoryByCategory(branchId)
      ]);

      // Process summary response (includes totalQuantity, totalValue, activeEndgoods, etc.)
      if (results[0].status === 'fulfilled' && results[0].value.success && results[0].value.data) {
        setSummary(results[0].value.data);
      }

      // Process category breakdown response
      if (results[1].status === 'fulfilled' && results[1].value.success && results[1].value.data) {
        setCategorySummary(results[1].value.data);
      }

      // Check if any requests failed
      const hasFailures = results.some(result => result.status === 'rejected');
      if (hasFailures) {
        const failedCount = results.filter(result => result.status === 'rejected').length;
        if (failedCount === results.length) {
          toast.error('Failed to load dashboard data');
        } else {
          toast.warning(`Some dashboard data could not be loaded (${failedCount} of ${results.length} requests failed)`);
        }
      }

    } catch (error) {
      console.error('Error loading finished goods dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadExtendedInventory = async () => {
    try {
      setExtendedLoading(true);
      const branchId = selectedBranch || undefined;
      const response = await finishedGoodsInventoryApiService.getFinishedGoodsInventoryExtendedView(branchId);
      if (response.success && response.data) {
        const sorted = [...response.data].sort((a, b) => {
          const totalA = (a.wipQuantity ?? 0) + (a.readyToPackQuantity ?? 0) + (a.packedQuantity ?? 0);
          const totalB = (b.wipQuantity ?? 0) + (b.readyToPackQuantity ?? 0) + (b.packedQuantity ?? 0);
          return totalB - totalA;
        });
        setExtendedInventory(sorted);
      } else {
        setExtendedInventory([]);
      }
    } catch (error) {
      setExtendedInventory([]);
    } finally {
      setExtendedLoading(false);
    }
  };

  const handleExtendedExport = () => {
    if (filteredExtendedInventory.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Endgood Name',
      'Endgood Category',
      'WIP Qty',
      'Ready to Pack Qty',
      'Packed Qty',
      'Packed Boxes',
      'Total Qty',
      'Days Since Last Activity'
    ];

    const rows = filteredExtendedInventory.map((item) => {
      const totalQty =
        (item.wipQuantity ?? 0) +
        (item.readyToPackQuantity ?? 0) +
        (item.packedQuantity ?? 0);
      return [
        item.endgoodName || 'N/A',
        item.categoryName || 'Uncategorized',
        (item.wipQuantity ?? 0).toString(),
        (item.readyToPackQuantity ?? 0).toString(),
        (item.packedQuantity ?? 0).toString(),
        (item.packedBoxes ?? 0).toString(),
        totalQty.toString(),
        (item.daysSinceLastActivity ?? 0).toString()
      ];
    });

    const csvRows = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
      )
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const categorySuffix = selectedCategory ? `_${selectedCategory.replace(/\s+/g, '-')}` : '';
    link.href = url;
    link.download = `fg-inventory-extended${categorySuffix}_${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAgingExport = () => {
    if (filteredAgingPivotRows.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Endgood Name', 'Endgood Category', ...agingBucketOrder];
    const rows = filteredAgingPivotRows.map((row) => ([
      row.endgoodName,
      row.categoryName,
      ...agingBucketOrder.map((bucket) => row.buckets[bucket] || 0),
    ]));

    const csvRows = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
      )
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const categorySuffix = selectedCategory ? `_${selectedCategory.replace(/\s+/g, '-')}` : '';
    link.href = url;
    link.download = `fg-aging-details${categorySuffix}_${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const loadAgingSummary = async () => {
    try {
      setAgingLoading(true);
      const branchId = selectedBranch || undefined;
      const categoryId = categorySummary.find(
        item => (item.categoryName || 'Uncategorized') === selectedCategory
      )?.categoryId;
      const response = await finishedGoodsInventoryApiService.getInventoryAgingSummary(branchId, categoryId);
      if (response.success && response.data) {
        setAgingRows(response.data);
      } else {
        setAgingRows([]);
      }
    } catch (error) {
      setAgingRows([]);
    } finally {
      setAgingLoading(false);
    }
  };

  const loadAgingDetails = async () => {
    try {
      setAgingDetailLoading(true);
      const branchId = selectedBranch || undefined;
      const categoryId = categorySummary.find(
        item => (item.categoryName || 'Uncategorized') === selectedCategory
      )?.categoryId;
      const response = await finishedGoodsInventoryApiService.getInventoryAgingDetails(branchId, categoryId);
      if (response.success && response.data) {
        setAgingDetailRows(response.data);
      } else {
        setAgingDetailRows([]);
      }
    } catch (error) {
      setAgingDetailRows([]);
    } finally {
      setAgingDetailLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const categoryChartData = useMemo(() => {
    return categorySummary
      .map((item) => ({
        name: item.categoryName || 'Uncategorized',
        quantity: item.quantity || 0,
        totalValue: item.totalValue ?? item.value ?? 0
      }))
      .filter((item) => item.quantity > 0)
      .sort((a, b) => b.quantity - a.quantity);
  }, [categorySummary]);

  const agingBucketOrder = ['0-30', '31-60', '61-90', '91-120', '>120'];
  const agingBucketStyles: Record<string, string> = {
    '0-30': 'bg-emerald-50 text-emerald-700',
    '31-60': 'bg-blue-50 text-blue-700',
    '61-90': 'bg-amber-50 text-amber-700',
    '91-120': 'bg-rose-50 text-rose-700',
    '>120': 'bg-rose-50 text-rose-700',
  };

  const filteredAgingDetailRows = useMemo(() => {
    if (!selectedCategory) return agingDetailRows;
    return agingDetailRows.filter(
      row => (row.categoryName || 'Uncategorized') === selectedCategory
    );
  }, [agingDetailRows, selectedCategory]);

  const agingPivotRows = useMemo(() => {
    if (filteredAgingDetailRows.length === 0) return [];

    const normalizeBucket = (row: FinishedGoodsInventoryAgingDetail): string => {
      return (
        row.bucketLabel ||
        row.agingBucket ||
        row.ageBucket ||
        row.bucket ||
        row.label ||
        'Unknown'
      );
    };

    const grouped = filteredAgingDetailRows.reduce<
      Record<
        string,
        {
          key: string;
          endgoodId?: number;
          endgoodName: string;
          categoryName: string;
          buckets: Record<string, number>;
          totalQty: number;
        }
      >
    >(
      (acc, row) => {
        const endgoodName = row.endgoodName || 'N/A';
        const categoryName = row.categoryName || 'Uncategorized';
        const keyBase = row.endgoodId ?? endgoodName;
        const key = `${keyBase}__${categoryName}`;
        if (!acc[key]) {
          acc[key] = {
            key,
            endgoodId: row.endgoodId,
            endgoodName,
            categoryName,
            buckets: agingBucketOrder.reduce<Record<string, number>>((bucketAcc, bucket) => {
              bucketAcc[bucket] = 0;
              return bucketAcc;
            }, {}),
            totalQty: 0,
          };
        }

        const bucket = normalizeBucket(row);
        if (agingBucketOrder.includes(bucket)) {
          const quantity = row.totalQuantity ?? row.quantity ?? 0;
          acc[key].buckets[bucket] += quantity;
          acc[key].totalQty += quantity;
        }
        return acc;
      },
      {}
    );

    return Object.values(grouped).sort((a, b) => {
      if (b.totalQty !== a.totalQty) {
        return b.totalQty - a.totalQty;
      }
      const nameCompare = a.endgoodName.localeCompare(b.endgoodName);
      if (nameCompare !== 0) return nameCompare;
      return a.categoryName.localeCompare(b.categoryName);
    });
  }, [filteredAgingDetailRows, agingBucketOrder]);

  const filteredSummary = useMemo(() => {
    if (!selectedCategory) return summary;
    const categoryItem = categorySummary.find(
      item => (item.categoryName || 'Uncategorized') === selectedCategory
    );
    return {
      totalQuantity: categoryItem?.quantity ?? 0,
      totalValue: categoryItem?.totalValue ?? categoryItem?.value ?? 0,
      activeEndgoods: summary?.activeEndgoods ?? 0,
      lowStockCount: summary?.lowStockCount ?? 0,
      overstockCount: summary?.overstockCount ?? 0,
    } as FinishedGoodsInventorySummaryResponse;
  }, [categorySummary, selectedCategory, summary]);

  const filteredExtendedInventory = useMemo(() => {
    let data = extendedInventory;
    if (selectedCategory) {
      data = data.filter(
        item => (item.categoryName || 'Uncategorized') === selectedCategory
      );
    }
    const term = extendedSearch.trim().toLowerCase();
    if (term) {
      data = data.filter(item => {
        const name = (item.endgoodName || '').toLowerCase();
        const cat = (item.categoryName || '').toLowerCase();
        return name.includes(term) || cat.includes(term);
      });
    }
    return data;
  }, [extendedInventory, selectedCategory, extendedSearch]);

  const extendedTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredExtendedInventory.length / EXTENDED_PAGE_SIZE));
  }, [filteredExtendedInventory.length]);

  const pagedExtendedInventory = useMemo(() => {
    const startIndex = (extendedPage - 1) * EXTENDED_PAGE_SIZE;
    return filteredExtendedInventory.slice(startIndex, startIndex + EXTENDED_PAGE_SIZE);
  }, [filteredExtendedInventory, extendedPage]);

  const filteredAgingPivotRows = useMemo(() => {
    const term = agingSearch.trim().toLowerCase();
    if (!term) return agingPivotRows;
    return agingPivotRows.filter(row => {
      const name = (row.endgoodName || '').toLowerCase();
      const cat = (row.categoryName || '').toLowerCase();
      return name.includes(term) || cat.includes(term);
    });
  }, [agingPivotRows, agingSearch]);

  const agingTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredAgingPivotRows.length / AGING_PAGE_SIZE));
  }, [filteredAgingPivotRows.length]);

  const pagedAgingPivotRows = useMemo(() => {
    const startIndex = (agingPage - 1) * AGING_PAGE_SIZE;
    return filteredAgingPivotRows.slice(startIndex, startIndex + AGING_PAGE_SIZE);
  }, [filteredAgingPivotRows, agingPage]);

  const deadStockQuantity = useMemo(() => {
    const buckets = ['91-120', '>120'];
    return agingPivotRows.reduce((sum, row) => {
      const bucketSum = buckets.reduce((acc, bucket) => acc + (row.buckets[bucket] || 0), 0);
      return sum + bucketSum;
    }, 0);
  }, [agingPivotRows]);

  const categoryChartHeight = useMemo(() => {
    // Make sure the chart uses the available white space nicely, even with many categories
    const rows = Math.max(1, categoryChartData.length);
    return Math.min(520, 80 + rows * 34);
  }, [categoryChartData.length]);

  const renderCategoryTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0]?.payload;
    if (!data) return null;
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-md">
        <div className="font-semibold text-gray-900">{data.name}</div>
        <div className="text-gray-700">Qty: {formatNumber(data.quantity)}</div>
        <div className="text-gray-700">Value: {formatCurrency(data.totalValue)}</div>
        <div className="text-xs text-gray-500 mt-1">Click to filter</div>
      </div>
    );
  };

  const agingChartData = useMemo(() => {
    const normalizeBucket = (row: AgingRow): string => {
      return (
        row.agingBucket ||
        row.bucketLabel ||
        row.ageBucket ||
        row.bucket ||
        row.label ||
        'Unknown'
      );
    };

    const filtered = selectedCategory
      ? agingRows.filter(row => (row.categoryName || 'Uncategorized') === selectedCategory)
      : agingRows;

    const order = ['0-30', '31-60', '61-90', '91-120', '>120', 'Unknown'];
    const grouped = filtered.reduce<Record<string, { packedQty: number; wipQty: number }>>((acc, row) => {
      const bucket = normalizeBucket(row);
      const quantity = row.totalQuantity ?? row.quantity ?? 0;
      if (!acc[bucket]) {
        acc[bucket] = { packedQty: 0, wipQty: 0 };
      }
      if (row.inventoryType === 'PACKED') {
        acc[bucket].packedQty += quantity;
      } else if (row.inventoryType === 'WIP') {
        acc[bucket].wipQty += quantity;
      }
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([bucket, totals]) => ({
        bucket,
        ...totals,
        totalQuantity: totals.packedQty + totals.wipQty,
      }))
      .filter(item => item.totalQuantity > 0)
      .sort((a, b) => {
        const aIndex = order.indexOf(a.bucket);
        const bIndex = order.indexOf(b.bucket);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
  }, [agingRows, selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-3 text-gray-600">Loading Finished Goods Dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Finished Goods Inventory Dashboard</CardTitle>
                  <p className="text-gray-600 mt-1">Real-time visibility into finished goods inventory</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
        </Card>

      {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Total Quantity</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(filteredSummary?.totalQuantity)}</div>
            <p className="text-xs text-muted-foreground mt-1">Finished goods in stock</p>
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(filteredSummary?.totalValue)}</div>
            <p className="text-xs text-muted-foreground mt-1">Inventory valuation</p>
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Active SKUs</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(filteredSummary?.activeEndgoods)}</div>
            <p className="text-xs text-muted-foreground mt-1">Endgoods with inventory</p>
          </CardContent>
        </Card>

        </div>

      {/* Category Distribution + Age Bucket Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-row items-center gap-2">
            <Package2 className="h-5 w-5 text-primary-600" />
            <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800">Category Distribution</CardTitle>
              {selectedCategory && (
                <Button variant="outline" size="sm" onClick={() => setSelectedCategory(null)}>
                  Clear filter ({selectedCategory})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {categoryChartData.length > 0 ? (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={categoryChartHeight}>
                  <BarChart
                    data={categoryChartData}
                    layout="vertical"
                    margin={{ top: 8, right: 24, bottom: 8, left: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) => formatNumber(v)}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={180}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={renderCategoryTooltip} />
                    <Bar
                      dataKey="quantity"
                      radius={[6, 6, 6, 6]}
                      onClick={(data: any) => {
                        const nextCategory = data?.name;
                        if (nextCategory) {
                          setSelectedCategory(nextCategory);
                        }
                      }}
                    >
                      {categoryChartData.map((item, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          opacity={!selectedCategory || selectedCategory === item.name ? 1 : 0.4}
                          cursor="pointer"
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[240px] text-muted-foreground">
                No category data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-row items-center gap-2">
            <Clock className="h-5 w-5 text-primary-600" />
            <CardTitle className="text-lg font-semibold text-gray-800">Age Bucket Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            {agingLoading ? (
              <div className="flex items-center justify-center h-[240px] text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading aging summary...
              </div>
            ) : agingChartData.length > 0 ? (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={agingChartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatNumber(value || 0),
                        name === 'packedQty' ? 'Packed' : 'WIP',
                      ]}
                    />
                    <Bar dataKey="packedQty" radius={[6, 6, 0, 0]} fill="#60A5FA" />
                    <Bar dataKey="wipQty" radius={[6, 6, 0, 0]} fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground mt-3">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-sm bg-[#60A5FA]" />
                    Packed
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-sm bg-[#F59E0B]" />
                    WIP
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[240px] text-muted-foreground">
                No aging summary data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tables Section */}

      <Tabs defaultValue="extended" className="mt-6">
        <TabsList className="rounded-full bg-gray-100 p-1 gap-1">
          <TabsTrigger
            value="extended"
            className="rounded-full px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            Extended Table
          </TabsTrigger>
          <TabsTrigger
            value="aging"
            className="rounded-full px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            Aging Table
          </TabsTrigger>
        </TabsList>

        <TabsContent value="extended">
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Finished Goods Inventory (Extended){selectedCategory ? `: ${selectedCategory}` : ''}
                </CardTitle>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="hidden md:block text-xs text-gray-500">
                    {filteredExtendedInventory.length} items
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search endgood or category..."
                      value={extendedSearch}
                      onChange={(e) => setExtendedSearch(e.target.value)}
                      className="pl-9 h-8 w-56 text-sm"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExtendedExport}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {extendedLoading ? (
                <div className="flex items-center justify-center h-40 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading extended inventory...
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Endgood Name</TableHead>
                          <TableHead>Endgood Category</TableHead>
                          <TableHead>WIP Qty</TableHead>
                          <TableHead>Ready to Pack Qty</TableHead>
                          <TableHead>Packed Qty</TableHead>
                          <TableHead>Packed Boxes</TableHead>
                          <TableHead>Total Qty</TableHead>
                          <TableHead>Days Since Last Packed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedExtendedInventory.length > 0 ? (
                          pagedExtendedInventory.map((item, index) => (
                            <TableRow key={`${item.endgoodId ?? 'row'}-${index}`}>
                              <TableCell className="font-medium">
                                {item.endgoodId ? (
                                  <a
                                    href={`/endgoods/${item.endgoodId}/view`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary-700 hover:underline"
                                  >
                                    {item.endgoodName || 'N/A'}
                                  </a>
                                ) : (
                                  item.endgoodName || 'N/A'
                                )}
                              </TableCell>
                              <TableCell>{item.categoryName || 'Uncategorized'}</TableCell>
                              <TableCell>{formatNumber(item.wipQuantity ?? 0)}</TableCell>
                              <TableCell>{formatNumber(item.readyToPackQuantity ?? 0)}</TableCell>
                              <TableCell>{formatNumber(item.packedQuantity ?? 0)}</TableCell>
                              <TableCell>{formatNumber(item.packedBoxes ?? 0)}</TableCell>
                              <TableCell className="font-semibold">
                                {formatNumber((item.wipQuantity ?? 0) + (item.readyToPackQuantity ?? 0) + (item.packedQuantity ?? 0))}
                              </TableCell>
                              <TableCell>
                                {item.daysSinceLastActivity === null || item.daysSinceLastActivity === undefined
                                  ? 'N/A'
                                  : formatNumber(item.daysSinceLastActivity)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground">
                              No extended inventory data available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {extendedTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-muted-foreground">
                        Page {extendedPage} of {extendedTotalPages}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExtendedPage(prev => Math.max(1, prev - 1))}
                          disabled={extendedPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExtendedPage(prev => Math.min(extendedTotalPages, prev + 1))}
                          disabled={extendedPage === extendedTotalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aging">
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary-600" />
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    FG Aging & Dead Stock Analysis{selectedCategory ? `: ${selectedCategory}` : ''}
                  </CardTitle>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="hidden md:block text-xs text-gray-500">
                    {filteredAgingPivotRows.length} items
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search endgood or category..."
                      value={agingSearch}
                      onChange={(e) => setAgingSearch(e.target.value)}
                      className="pl-9 h-8 w-56 text-sm"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAgingExport}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Age distribution and dead stock identification
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                <div className="font-semibold">Dead Stock Alert</div>
                <div className="text-sm">
                  Items aged 90+ days: {formatNumber(deadStockQuantity)}
                </div>
              </div>
              {agingDetailLoading ? (
                <div className="flex items-center justify-center h-[240px] text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading aging details...
                </div>
              ) : agingPivotRows.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Endgood Name</TableHead>
                          <TableHead>Endgood Category</TableHead>
                          <TableHead className="text-right">Total Qty</TableHead>
                          {agingBucketOrder.map((bucket) => (
                            <TableHead
                              key={bucket}
                              className={agingBucketStyles[bucket] || undefined}
                            >
                              {bucket}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedAgingPivotRows.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell className="font-medium">
                              {row.endgoodId ? (
                                <a
                                  href={`/endgoods/${row.endgoodId}/view`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary-700 hover:underline"
                                >
                                  {row.endgoodName}
                                </a>
                              ) : (
                                row.endgoodName
                              )}
                            </TableCell>
                            <TableCell>{row.categoryName}</TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatNumber(row.totalQty || 0)}
                            </TableCell>
                            {agingBucketOrder.map((bucket) => (
                              <TableCell
                                key={bucket}
                                className={agingBucketStyles[bucket] || undefined}
                              >
                                {formatNumber(row.buckets[bucket] || 0)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {agingTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-muted-foreground">
                        Page {agingPage} of {agingTotalPages}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAgingPage(prev => Math.max(1, prev - 1))}
                          disabled={agingPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAgingPage(prev => Math.min(agingTotalPages, prev + 1))}
                          disabled={agingPage === agingTotalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-[240px] text-muted-foreground">
                  No aging details available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      </div>
    </div>
  );
};

export default FinishedGoodsDashboard;

